import { GOLD, ageSteps, waitingSince, type AgeableTask, type BountyBreakdown } from "../shared/bounty.ts";
import { formatAgo } from "../shared/when.ts";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_MS = 86_400_000;

// The day and time, and how long ago, live in shared/when.ts so both clients say them the same way.
export { formatDateTime, formatRelative, formatAgo } from "../shared/when.ts";

/** A day without the time, for a one-line record: "Sep 22". */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface Ageable {
  created_at: string;
  due_at: string | null;
  urgent: number;
  schedule_id: string | null;
  created_by: string;
}

// The single definition lives with the bounty formula so ageing and payout can never drift apart.
export { waitingSince };

/** "1,000 gold": with a thousands separator, as the wood amounts and the trade buttons read. */
export function formatGold(n: number): string {
  return `${n.toLocaleString("en-US")} gold`;
}

/** "1,250 of 5,000 wood": a part of a whole with thousands separators, in any currency's unit. */
export function formatAmountOf(part: number, whole: number, unit: string): string {
  return `${part.toLocaleString("en-US")} of ${whole.toLocaleString("en-US")} ${unit}`;
}

/** "1,250 of 5,000 wood": the upgrade bars' line (docs/upgrades.md). Upgrades cost wood, not gold. */
export function formatWoodOf(part: number, whole: number): string {
  return formatAmountOf(part, whole, "wood");
}

/** "2 h 10 m", "45 m", "3 d": a duration in ms as a short, rounded label. Null shows as a dash. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "–";
  const abs = Math.max(0, ms);
  const min = 60_000;
  const hour = 60 * min;
  if (abs >= DAY_MS) return `${Math.round(abs / DAY_MS)} d`;
  if (abs >= hour) {
    const h = Math.floor(abs / hour);
    const m = Math.round((abs % hour) / min);
    return m > 0 ? `${h} h ${m} m` : `${h} h`;
  }
  return `${Math.max(1, Math.round(abs / min))} m`;
}

/** "2 days", "4 hours": the time a bounty's ageing steps add up to. */
function describeAgeWait(b: Pick<BountyBreakdown, "ageSteps" | "agePeriodMs">): string {
  const ms = b.ageSteps * b.agePeriodMs;
  if (ms % DAY_MS === 0) {
    const days = ms / DAY_MS;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  const hours = Math.round(ms / 3_600_000);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

/** Only show modifiers that explain why a task pays more than usual. */
export function describeBounty(b: BountyBreakdown, opts: { terse?: boolean } = {}): string {
  const parts: string[] = [];
  // Big job and urgent never stack, so at most one of these is set: whichever paid.
  if (b.bigJob) parts.push("big job");
  if (b.urgent) parts.push(`urgent ×${GOLD.urgentMultiplier}`);
  // The bounty streak (docs/bounties.md "Streak") is named by its word alone, only when it won.
  if (b.streak) parts.push("streak");
  // The quick bonus and a gold rush show as the flame badge, not as text; while either (or the
  // streak) applies the waiting multiplier is not in force, so that part is left out too. Terse
  // drops the "waiting N days" words for a line that already says how long the task has waited.
  // A daily chore ages for colour but pays flat (ageMultiplier stays 1), so it gets no note.
  const speedApplies = b.quickMultiplier !== null || b.flashMultiplier !== null || !!b.streak;
  if (!speedApplies && b.ageSteps >= 1 && b.ageMultiplier > 1) parts.push(opts.terse ? `×${b.ageMultiplier}` : `waiting ${describeAgeWait(b)} ×${b.ageMultiplier}`);
  // Stale gold (GOLD.stale): flat gold for each day past the top multiplier, whichever bonus won.
  if (b.staleGold) parts.push(opts.terse ? `+${b.staleGold}` : `+${b.staleGold} gold`);
  return parts.join(" · ");
}

/**
 * Attention level, 0..3: normal, orange, red, red with an exclamation. One
 * step per ageing period (a day for most tasks, two hours for daily chores,
 * see AGE_PERIOD_MS). Urgent tasks are level 3 from the start.
 */
export type AgeLevel = 0 | 1 | 2 | 3;
export function ageLevel(task: AgeableTask & Pick<Ageable, "urgent">, now = Date.now()): AgeLevel {
  if (task.urgent) return 3;
  return Math.min(3, ageSteps(task, now)) as AgeLevel;
}

export function describeWaiting(task: AgeableTask, now = Date.now()): string {
  const since = waitingSince(task);
  const days = Math.floor((now - new Date(since).getTime()) / DAY_MS);
  if (days >= 1) return `Waiting ${days} day${days === 1 ? "" : "s"}`;
  // A chore is posted by its schedule; a hand-added task was added by someone. Neither is "due".
  return `${task.schedule_id ? "Posted" : "Added"} ${formatAgo(since, now)}`;
}

export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

export function describeCadence(s: { cadence: string; time_of_day: string; day_of_week: number | null; day_of_month: number | null }): string {
  const time = formatTime(s.time_of_day);
  switch (s.cadence) {
    case "daily":
      return `Every day at ${time}`;
    case "weekly":
      return `Every ${DAY_NAMES[s.day_of_week ?? 0]} at ${time}`;
    case "monthly":
      return `Monthly on the ${ordinal(s.day_of_month ?? 1)} at ${time}`;
    default:
      return s.cadence;
  }
}

export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** The original household's id (server/households.ts DEFAULT_HOUSEHOLD): its chores are Omie's. */
const ORIGINAL_HOUSEHOLD = "omies";

/** The active household, as AuthState names it (app.tsx sets it once auth is known). */
let activeHousehold: { id: string; name: string } | null = null;
export function setActiveHousehold(h: { id: string; name: string } | null | undefined): void {
  activeHousehold = h ? { id: h.id, name: h.name } : null;
}

/**
 * Who a chore is from: the house that set the schedule, named as the household calls itself
 * ("Hay House"). "Omie" for the original household and while the name is not known yet. A
 * hand-added task shows its creator. The real creator of a chore stays in the DB.
 */
export function displayCreator(task: { created_by: string; schedule_id: string | null }, house: { id: string; name: string } | null = activeHousehold): string {
  if (!task.schedule_id) return task.created_by;
  if (!house || house.id === ORIGINAL_HOUSEHOLD || !house.name.trim()) return "Omie";
  return house.name.trim();
}

/** "YYYY-MM" of `now` in the IANA zone `tz` (the device's own when it cannot be read). */
export function monthKeyIn(tz: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit" }).formatToParts(now);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    if (y && m) return `${y}-${m}`;
  } catch {
    /* an unknown zone: fall through */
  }
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export { DAY_NAMES };
